import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStrategies } from '../api';

const REFRESH_INTERVAL = 10000;

export default function Strategies() {
  const [stratData, setStratData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const res = await fetchStrategies();
      if (res.data?.strategies) setStratData(res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading && !stratData) {
    return <div className="loading"><div className="spinner" /><p>Loading strategies...</p></div>;
  }

  const strategies = stratData?.strategies || [];
  const totalPnl = strategies.reduce((sum, s) => sum + (s.pnl || 0), 0);
  const activeCount = strategies.filter(s => s.state === 'ACTIVE').length;
  const liveCount = strategies.filter(s => !s.paper_trading).length;

  const niftyStrats = strategies.filter(s => s.index === 'Nifty');
  const sensexStrats = strategies.filter(s => s.index === 'Sensex');

  const renderCard = (strat) => (
    <div
      key={strat.name}
      className={`sl-card ${strat.state === 'ACTIVE' ? 'sl-card-active' : ''}`}
      onClick={() => navigate(`/strategies/${strat.name}`)}
    >
      <div className="sl-card-header">
        <div className="sl-card-left">
          <span className={`strat-state-dot strat-dot-${strat.state?.toLowerCase()}`} />
          <div className="sl-card-title">
            <h3>{strat.display_name || strat.strategy_type}</h3>
            <p>{strat.description}</p>
          </div>
        </div>
        <div className="sl-card-right">
          <div className={`sl-pnl ${(strat.pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
            {(strat.pnl || 0) >= 0 ? '+' : ''}{(strat.pnl || 0).toFixed(2)}
            <span className="sl-pnl-unit">pts/lot</span>
          </div>
          <span className={`strat-state-badge strat-state-${strat.state?.toLowerCase()}`}>{strat.state}</span>
        </div>
      </div>
      <div className="sl-card-meta">
        <span className="sl-meta-chip">📅 {strat.trading_day}</span>
        <span className="sl-meta-chip">⏱ {strat.start_time} – {strat.exit_time}</span>
        <span className="sl-meta-chip">🎯 {strat.adjustment_points}pt adj</span>
        <span className="sl-meta-chip">📊 {strat.lots || strat.params?.lots || 1} lot{(strat.lots || strat.params?.lots || 1) > 1 ? 's' : ''}</span>
        <span className="sl-meta-chip">👤 {strat.account_name || 'Paper'}</span>
        <span className={`sl-meta-chip ${strat.paper_trading ? 'chip-paper' : 'chip-live'}`}>
          {strat.paper_trading ? '📝 Paper' : '🔴 Live'}
        </span>
      </div>
      {strat.last_action && (
        <div className="sl-card-footer">
          <span className="sl-last-action">Last: {strat.last_action}</span>
          {strat.last_action_time && <span className="sl-last-time">{strat.last_action_time}</span>}
        </div>
      )}
    </div>
  );

  return (
    <div className="page-strategies">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Strategies</h1>
          <p className="page-subtitle">Monitor and manage all trading strategies</p>
        </div>
      </div>

      {/* Stats Bar */}
      <section className="sl-stats-bar">
        <div className="sl-stat-item">
          <div className={`sl-stat-mode ${liveCount > 0 ? 'live' : ''}`}>
            <span className={`mode-dot ${liveCount > 0 ? 'live-mode' : 'paper'}`} />
            {liveCount > 0 ? `${liveCount} LIVE` : 'ALL PAPER'}
          </div>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Expiry</span>
          <span className="sl-stat-value">{stratData?.expiry || '—'}</span>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Active</span>
          <span className="sl-stat-value sl-active-val">{activeCount} / {strategies.length}</span>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Total P&L</span>
          <span className={`sl-stat-value ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
            {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)} pts
          </span>
        </div>
      </section>

      {/* Nifty Group */}
      {niftyStrats.length > 0 && (
        <section className="sl-group">
          <h2 className="sl-group-title"><span className="sl-group-icon">🇮🇳</span> Nifty Strategies</h2>
          <div className="sl-card-list">{niftyStrats.map(renderCard)}</div>
        </section>
      )}

      {/* Sensex Group */}
      {sensexStrats.length > 0 && (
        <section className="sl-group">
          <h2 className="sl-group-title"><span className="sl-group-icon">📊</span> Sensex Strategies</h2>
          <div className="sl-card-list">{sensexStrats.map(renderCard)}</div>
        </section>
      )}

      {strategies.length === 0 && (
        <div className="dash-empty">
          No strategies configured yet. Go to <button className="dash-link" onClick={() => navigate('/settings')}>Settings</button> to add strategy instances.
        </div>
      )}
    </div>
  );
}
